import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check } from '@mui/icons-material';
import { zIndex } from '../theme/tokens';

export const isAnimEnabled = () =>
  localStorage.getItem('animationsEnabled') !== 'false' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// calPhase: 'hidden' → 'before' → 'subtract' → 'after'
const SuccessOverlay = ({ entry, animate }) => {
  const theme = useTheme();
  const [calPhase, setCalPhase] = useState('hidden');
  const [revealed, setRevealed] = useState(!animate);

  // Same reveal choreography as before, driven by state + CSS transitions
  // instead of bespoke @keyframes, so it uses the theme's celebration
  // gradient tokens rather than raw hex.
  useEffect(() => {
    if (!animate) {
      setRevealed(true);
      return;
    }
    setRevealed(false);
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  const showCalAnim =
    animate &&
    entry?.prevRemaining !== undefined && entry?.prevRemaining !== null &&
    entry?.newRemaining !== undefined && entry?.newRemaining !== null;

  useEffect(() => {
    if (!showCalAnim) { setCalPhase('hidden'); return; }
    setCalPhase('hidden');
    const t1 = setTimeout(() => setCalPhase('before'),   500);
    const t2 = setTimeout(() => setCalPhase('subtract'), 1050);
    const t3 = setTimeout(() => setCalPhase('after'),    1650);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [showCalAnim, entry]);

  const newRem = entry?.newRemaining ?? 0;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: zIndex.overlay,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: 'background.default',
        opacity: revealed ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* ── Grüner Kreis ── */}
      <Box sx={{
        width: 96, height: 96, borderRadius: '50%',
        background: `linear-gradient(135deg, ${theme.palette.celebration.gradientStart} 0%, ${theme.palette.celebration.gradientEnd} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 8px 32px ${alpha(theme.palette.celebration.gradientEnd, 0.35)}`,
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'scale(1)' : 'scale(0)',
        transition: animate
          ? 'transform 0.4s 0.05s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s 0.05s ease'
          : 'none',
      }}>
        <Check sx={{ fontSize: 52, color: 'common.white' }} />
      </Box>

      {/* ── "Erfasst!" ── */}
      <Typography variant="h5" sx={{
        mt: 3,
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(10px)',
        transition: animate ? 'transform 0.35s 0.25s ease, opacity 0.35s 0.25s ease' : 'none',
      }}>
        Erfasst!
      </Typography>

      {/* ── Name & kcal ── */}
      {entry && (
        <Typography variant="body2" color="text.secondary" sx={{
          mt: 0.75,
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(10px)',
          transition: animate ? 'transform 0.35s 0.35s ease, opacity 0.35s 0.35s ease' : 'none',
        }}>
          {entry.name} · {entry.kcal} kcal
        </Typography>
      )}

      {/* ── Kalorien-Animation ── */}
      {showCalAnim && (
        <Box sx={{
          mt: 3.5,
          position: 'relative', height: 88,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 240,
        }}>
          {/* Phase 1: Vorher */}
          <Box sx={{
            position: 'absolute', textAlign: 'center',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            opacity:   calPhase === 'before' ? 1 : 0,
            transform: calPhase === 'before' ? 'translateY(0)' : 'translateY(-14px)',
            pointerEvents: 'none',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
              VORHER FREI
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.1, color: 'text.primary' }}>
              {entry.prevRemaining}
            </Typography>
            <Typography variant="caption" color="text.secondary">kcal</Typography>
          </Box>

          {/* Phase 2: Abzug */}
          <Box sx={{
            position: 'absolute', textAlign: 'center',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            opacity:   calPhase === 'subtract' ? 1 : 0,
            transform: calPhase === 'subtract' ? 'translateY(0)' : 'translateY(14px)',
            pointerEvents: 'none',
          }}>
            <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.1, color: 'error.main' }}>
              − {entry.kcal}
            </Typography>
            <Typography variant="caption" color="text.secondary">kcal abgezogen</Typography>
          </Box>

          {/* Phase 3: Danach */}
          <Box sx={{
            position: 'absolute', textAlign: 'center',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
            opacity:   calPhase === 'after' ? 1 : 0,
            transform: calPhase === 'after' ? 'translateY(0)' : 'translateY(14px)',
            pointerEvents: 'none',
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5 }}>
              JETZT NOCH FREI
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{
              lineHeight: 1.1,
              color: newRem > 0 ? 'success.main' : 'error.main',
            }}>
              {Math.max(0, newRem)}
            </Typography>
            <Typography variant="caption" color="text.secondary">kcal</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SuccessOverlay;
